import { useCallback, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import { createUsersApi } from '../api/usersApi';
import type { CreatedUser, CreateUserPayload } from '../userTypes';

interface UseCreateUserResult {
  createUser: (payload: CreateUserPayload) => Promise<CreatedUser | null>;
  loading: boolean;
  error: ApiError | Error | null;
}

export function useCreateUser(): UseCreateUserResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => createUsersApi(client), [client]);
  const { notifyError } = useNotification();

  const [error, setError] = useState<ApiError | Error | null>(null);

  const createUser = useCallback(
    async (payload: CreateUserPayload): Promise<CreatedUser | null> => {
      try {
        setError(null);
        return await api.create(payload);
      } catch (err) {
        const e = err as ApiError | Error;
        setError(e);
        // Mutation : 5xx → snackbar. 4xx → exposé localement (formulaire gère l'affichage)
        if (ApiError.isApiError(e) && e.isServerError) notifyError(e);
        else if (!ApiError.isApiError(e)) notifyError(e);
        return null;
      }
    },
    [api, notifyError],
  );

  return { createUser, loading, error };
}
